//mypreset.ts
import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import { Preset } from '@primeuix/themes/types';

const customTheme: Preset = {
    primitive: {
        borderRadius: {
          none: "0",
          xs: "4px",
          sm: "4px",
          md: "4px",
          lg: "4px",
          xl: "4px"
        },
    },
    semantic: {
        primary: {
            50: '#ffffff',
            100: '#FAFAFA',
            200: '#F5F5F5',
            300: '#EEEEEE',
            400: '#E0E0E0',
            500: '#BDBDBD',
            600: '#9E9E9E',
            700: '#757575',
            800: '#616161',
            900: '#424242',
            950: '#212121',
        },
        colorScheme: {
            light: {
                primary: {
                    color: '#ff9100',
                },
                mask: {
                    color: "var(--black-dark)"
                },
                text: {
                    color: "#212529",
                    muted: {
                        color: "var(--text-color-secondary)",
                    }
                },
                formField: {
                    border: {
                        color: "var(--gray-400)"
                    },
                    hover: {
                        border: {
                            color: "var(--gray-400)"
                        }
                    },
                    placeholder: {
                        color: "var(--text-color-secondary)"
                    },
                    icon: {
                        color: "var(--text-color)",
                    },
                    disabled: {
                        background: "var(--gray-200)",
                        color: "var(--gray-600)",
                    },
                },
                overlay: {
                    select: {
                        border: {
                            color: "var(--gray-300)",
                        },
                        shadow:"none",
                    },
                },
                list: {
                    padding: ".5rem 0",
                    header: {
                        padding: ".75rem 1.5rem",
                    },
                    option: {
                        selected: {
                            background: "var(--primary-color)",
                            color: "white",
                            focus: {
                                background: "var(--primary-dark-color)",
                                color: "white"
                            }
                        },
                        focus: {
                            background: "var(--gray-200)",
                        },
                        border: {
                            radius: "none",
                        },
                    },
                },
                content: {
                    border: {
                        color: "var(--gray-300)",
                    },
                },
            }
        },
    },
    components: {
        badge: {
            root: {
                padding: "0"
            },
        },
        skeleton: {
            root: {
                background: "var(--gray-500) !important",
                animationBackground: "var(--gray-400) !important",
            }
        },
        button: {
            root: {
                primary: {
                    hoverBackground: "var(--primary-dark-color) !important",
                    hoverBorderColor: "var(--primary-dark-color) !important",
                    activeBackground: "var(--primary-darker-color) !important",
                    activeBorderColor: "var(--primary-darker-color) !important"
                }
            },
            outlined: {
                primary: {
                    hoverBackground: "var(--primary-dark-color-hover) !important",
                    activeBackground: "var(--primary-darker-color-hover) !important",
                    borderColor: "var(--primary-color) !important"
                }
            },
            text: {
                plain: {
                    hoverBackground: "var(--gray-200) !important"
                },
                primary: {
                    hoverBackground: "rgba(255, 145, 0, 0.04) !important",
                    activeBackground: "unset !important"
                },
            },

            css: () => `
                .rerun-part-button button {
                    width: 1rem !important;
                    height: 1rem;

                    .p-button-icon {
                        font-size: .7rem;
                    }
                }

                .p-button-icon-only::after {
                    content: none;
                }
            `
        },
        toggleswitch: {
            root: {
                checkedHoverBackground: "var(--primary-dark-color) !important"
            },
        },
        radiobutton: {
            root: {
                checkedHoverBackground: "var(--primary-dark-color) !important",
                checkedHoverBorderColor: "var(--primary-dark-color) !important",
            }
        },
        checkbox: {
            root: {
                checkedHoverBackground: "var(--primary-dark-color)",
                checkedHoverBorderColor: "var(--primary-dark-color)"
            }
        },
        progressspinner: {
            root: {
                colorOne: "var(--dark-gray) !important",
                colorTwo: "var(--black-dark) !important",
                colorThree: "var(--dark-gray) !important",
                colorFour: "var(--black-dark) !important",
            }
        },
        select: {
            dropdown: {
                color: "var(--text-color)"
            },
            css: () => `
                .p-select input {
                    font-size: var(--font-size);
                }
            `
        },
        chip: {
            root: {
                background: "var(--primary-color) !important",
                color: "white !important",
            },
            icon: {
                color: "white !important"
            },
            removeIcon: {
                color: "white !important"
            }
        },
        tooltip: {
            root: {
                background: "var(--text-color)",
            }
        },
        dialog: {
            header: {
                padding: ".5rem",
            },
            title: {
                fontSize: "1.15rem",
            },
            content: {
                padding: "1.5rem",
            },
            footer: {
                padding: ".5rem", 
            },
            css: () => `
                .p-dialog {
                    max-width: 90vw;
                    max-height: 90vh;
                    border: 0;
                }

                .p-dialog-header {
                    border: 0 0 1px 0 solid var(--gray-300);
                    border-top-left-radius: var(--border-radius);
                    border-top-right-radius: var(--border-radius);
                    background: var(--primary-color);
                    color: white;

                    .p-dialog-close-button {
                        padding: 0 !important;
                        color: white !important;
                        width: 2rem;
                        height: 2rem;

                        &:hover {
                            background: transparent !important;
                            color: white !important;
                        }
                    }
                }

                .p-dialog-footer {
                    border-top: 1px solid var(--gray-300);

                    .p-confirmdialog-reject-button, .p-confirmdialog-accept-button {
                        gap: 0;
                    }
                }
            `
        },
        tabs: {
            tab: {
                color: "var(--text-color)",
                borderWidth: "0 0 2px 0",
                hoverBorderColor: "#9ba2aa",
            },
            tablist: {
                borderWidth: "0 0 2px 0",
            },
        },
        accordion: {
            header: {
                padding: "1rem 1.25rem",
                background: "var(--gray-100)",
                color: "var(--text-color)",
                hoverBackground: "var(--gray-200)",
                activeHoverBackground: "var(--gray-200)",
                activeBackground: "var(--gray-100)",
                first: {
                    borderWidth: "1px",
                },
            },
            content: {
                padding: "1.25rem",
                borderWidth: "0 1px 1px",
            },
            panel: {
                borderWidth: "0",
            },
            css: () => `
                .p-accordioncontent-content {
                    border-bottom-left-radius: var(--border-radius);
                    border-bottom-right-radius: var(--border-radius);
                }
            `,
        },
        multiselect: {
            option: {
                selectedBackground: "none",
                selectedColor: "var(--text-color)",
                selectedFocusBackground: "var(--surface-b)",
                selectedFocusColor: "var(--text-color)"
            },
            css: () => `
                .p-multiselect-header {
                    background: var(--surface-b);
                }
            `
        },
        datepicker: {
            panel: {
              borderColor: "var(--gray-300)",
              shadow: "none",
              padding: "0"
            },
            header: {
              background: "var(--surface-b)",
              borderColor: "{content.border.color}",
              color: "{content.color}",
              padding: ".5rem"
            },
            title: {
              fontWeight: "bold"
            },
            selectMonth: {
              hoverBackground: "transparent",
              hoverColor: "var(--primary-color)",
              padding: "0.5rem",
            },
            selectYear: {
              hoverBackground: "transparent",
              hoverColor: "var(--primary-color)",
              padding: "0.5rem",
            },
            weekDay: {
              padding: "0.5rem",
              fontWeight: "bold",
              color: "{content.color}"
            },
            date: {
                hoverBackground: "var(--gray-200)",
                selectedBackground: "var(--primary-color)",
                rangeSelectedBackground: "var(--primary-color)",
                color: "{content.color}",
                hoverColor: "{content.color}",
                selectedColor: "white",
                rangeSelectedColor: "white",
                width: "2.5rem",
                height: "2.5rem",
                borderRadius: "var(--border-radius)",
                padding: "0.5rem",
            },
            monthView: {
              margin: "0.5rem"
            },
            month: {
              padding: "0.375rem",
              borderRadius: "{content.border.radius}"
            },
            yearView: {
              margin: "0.5rem"
            },
            year: {
              padding: "0.375rem",
              borderRadius: "{content.border.radius}"
            },
            buttonbar: {
              padding: "0.5rem 0 0 0",
              borderColor: "{content.border.color}"
            },
            timePicker: {
              padding: "0.5rem 0 0 0",
              borderColor: "{content.border.color}",
              gap: "0.5rem",
              buttonGap: "0.25rem"
            },
            dropdown: {
                background: "{surface.100}",
                hoverBackground: "{surface.200}",
                activeBackground: "{surface.300}",
                color: "{surface.600}",
                hoverColor: "{surface.700}",
                activeColor: "{surface.800}"
            },
            today: {
                background: "var(--gray-400) !important",
                color: "{content.color}"
            },
            css: () => `
                .p-datepicker-header {
                    .p-datepicker-title button {
                        font-size: var(--font-size);
                    }
                }

                .p-datepicker-header .p-datepicker-prev-button,
                .p-datepicker-header .p-datepicker-next-button,
                .p-datepicker-time-picker .p-datepicker-increment-button,
                .p-datepicker-time-picker .p-datepicker-decrement-button {
                    color: var(--text-color-secondary);

                    &:hover {
                        color: var(--text-color) !important;
                        background: transparent !important;
                    }
                }
            `
        },
        datatable: {
            header: {
                background: "var(--surface-b)",
                padding: "1rem",
                borderColor: "var(--gray-300)",
            },
            headerCell: {
                borderColor: "var(--gray-300)",
                padding: "1rem",
                hoverBackground: "var(--gray-200)",
                selectedColor: "var(--primary-color)",
            },
            bodyCell: {
                borderColor: "var(--gray-300)",
                padding: "1rem"
            },
            row: {
                background: "var(--surface-b)",
                hoverBackground: "var(--gray-200)",
                stripedBackground: "#fff !important",
            },
            sortIcon: {
                size: "1rem",
                color: "var(--text-color-secondary)",
                hoverColor: "var(--text-color)"
            },
            filter: {
                overlayPopover: {
                  borderColor: "var(--gray-300)",
                  shadow: "none",
                  padding: "0",
                  gap: "0.5rem",
                },
            },
            css: () => `
                .p-datatable-filter-operator {
                    background: var(--surface-b);
                    padding: .75rem 1.25rem;
                }

                .p-datatable-filter-rule {
                    padding-right: 1.25rem;
                    padding-left: 1.25rem;
                    padding-top: .75rem;

                    .p-multiselect-list-container p-tag {
                        background: none;
                        color: var(--text-color);
                        font-weight: 500;
                        font-size: 1rem;
                        padding: 0;
                    }
                }

                .p-datatable-filter-add-rule-button {
                    color: var(--primary-color) !important;

                    &:hover {
                        color: var(--primary-color) !important;
                        background: rgba(255, 145, 0, 0.04) !important;
                    }
                }

                .p-datatable-filter-buttonbar {
                    padding: 1.25rem;
                    padding-top: .75rem;
                }

                .p-datatable-hoverable .p-datatable-tbody > tr:not(.p-datatable-row-selected):hover {
                    background: var(--gray-200) !important;
                }
                
                .p-datatable-sortable-column {
                    .p-datatable-sort-icon {   
                        margin-left: .5rem;
                    }

                    .p-datatable-column-filter-button {
                        color: var(--text-color-secondary) !important;

                        &:hover {
                            color: var(--text-color) !important;
                            background: transparent !important;
                        }
                    }
                    
                }

                .p-datatable-column-filter-button {
                    padding: 0;
                    height: 2rem !important;
                    width: 2rem !important;
                }

                p-column-filter.filter-active {
                    .p-datatable-column-filter-button {
                        color: var(--primary-color) !important;
                    }
                }

                .p-datatable-tfoot {
                    background: var(--surface-b);
                    font-weight: bold;
                    padding: 1rem;
                    border: 1px solid var(--gray-300);
                }
            `
        },
        dataview: {
            css: () => `
                .p-dataview.p-dataview-list {
                    position: relative;

                    .p-dataview-loading-overlay.p-overlay-mask {
                        position: absolute;
                        z-index: 4;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }
                }
            `
        },
        toast: {
            root: {
                width: "25rem",
                borderWidth: "0",
            },
            icon: {
                size: "1.125rem"
            },
            content: {
                padding: "1rem",
            },
            summary: {
                fontWeight: "bold",
                fontSize: "1rem"
            },
            detail: {
                fontWeight: "500",
                fontSize: "1rem"
            },
            info: {
                background: "color-mix(in srgb, #039BE5, transparent 5%) !important",
                borderColor: "#039BE5 !important",
                color: "white !important",
                detailColor: "white !important",
                shadow: "none !important",
                closeButton: {
                    hoverBackground: "white !important",
                    focusRing: {
                        color: "white !important",
                        shadow: "none !important"
                    }
                }
            },
            success: {
                background: "color-mix(in srgb, #43A047, transparent 5%) !important",
                borderColor: "#43A047 !important",
                color: "white !important",
                detailColor: "white !important",
                shadow: "none !important",
                closeButton: {
                    hoverBackground: "white !important",
                    focusRing: {
                        color: "white !important",
                        shadow: "none !important"
                    }
                }
            },
            warn: {
                background: "color-mix(in srgb, #FFB300, transparent 5%) !important",
                borderColor: "#FFB300 !important",
                color: "var(--text-color) !important",
                detailColor: "var(--text-color) !important",
                shadow: "none !important",
                closeButton: {
                    hoverBackground: "white !important",
                    focusRing: {
                        color: "white !important",
                        shadow: "none !important"
                    }
                }
            },
            error: {
                background: "color-mix(in srgb, #E53935, transparent 5%) !important",
                borderColor: "#E53935 !important",
                color: "white !important",
                detailColor: "white !important",
                shadow: "none !important",
                closeButton: {
                    hoverBackground: "white !important",
                    focusRing: {
                        color: "white !important",
                        shadow: "none !important"
                    }
                }
            },
            css: () => `
                .p-toast-close-button {
                    color: white !important;
                }
                .p-toast-message-warn .p-toast-close-button {
                    color: var(--text-color) !important;
                }
            `
        },
        divider: {
            root: {
                borderColor: "var(--gray-300)"
            }
        },
        card: {
            root: {
                shadow: "0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)",
            },
            title: {
                fontSize: "1.5rem",
                fontWeight: "bold",
              },
            body: {
                padding: "1.5rem",
            },
            css: () => `
                .p-card-content {
                    padding: 1rem 0;
                }
            `,
        },
        menu: {
            root: {
                borderColor: "var(--gray-300)",
            },
            list: {
                padding: ".5rem 0",
            },
            item: {
                borderRadius: "none",
                padding: ".75rem 1rem",
                focusBackground: "var(--gray-200)",
            },
            css: () => `
                app-navbar {
                    .p-motion {
                        top: calc(var(--header-height) - .9rem) !important;
                    }

                    .p-menu-item-link-active {
                        color: var(--primary-color);
                        font-weight: bold;
                    }
                }
            `
        },
        popover: {
            css: () => `
                app-navbar {
                    .p-popover {
                        top: calc(var(--header-height) - .9rem) !important;
                        max-height: calc(100vh - var(--header-height));
                        overflow: auto;
                    }
                }
            `
        },
        menubar: {
            root: {
                background: "var(--black-dark)",
                gap: "0",
                borderRadius: "0",
                padding: ".5rem 1rem",
            },
            baseItem: {
                borderRadius: "0",
            },
            submenu: {
                background: "var(--black-dark)",
                borderColor: "rgba(0, 0, 0, 0.15)",
                shadow: "0 1px 6px 0 rgba(0, 0, 0, 0.1)",
                mobileIndent: "0",
                padding: ".5rem 0",
                borderRadius: "0",
                icon: {
                    color: "var(--text-color-secondary)",
                    focusColor: "var(--text-color-secondary)",
                    activeColor: "var(--text-color-secondary)",
                },
            },
            item: {
                focusBackground: "var(--darker-gray)",
                activeBackground: "var(--black-dark)",
                color: "var(--text-color-secondary)",
                focusColor: "var(--text-color-secondary)",
                activeColor: "var(--text-color-secondary)",
                padding: ".75rem 1rem",
                borderRadius: "0",
            },
            mobileButton: {
                size: "2rem",
                color: "var(--primary-color)",
                hoverColor: "var(--primary-color)",
                hoverBackground: "var(--darker-gray)",
            },
            css: () => `
                .p-menubar {
                    border: 0;

                    &.menubar-height {
                        min-height: var(--header-height);
                    }
                }

                .p-menubar-root-list {
                    .p-menubar-submenu {
                        .p-menubar-item-link {
                            padding-left: 2.25rem;
                        }

                        .p-menubar-submenu {
                            background: var(--dark-gray);

                            .p-menubar-item-link {
                                padding-left: 3.75rem;
                            }
                        }
                    }
                }
                

                .p-menubar-item-link-active {
                    color: var(--primary-color);
                    font-weight: bold;
                }

                .mobile-menu .p-menubar-end {
                    width: 100%;
                    margin-left: unset;
                }
            `
        },
        panelmenu: {
            root: {
                gap: "0"
            },
            panel: {
                background: "transparent",
                borderWidth: "0",
                borderRadius: "0",
                padding: "0",
                first: {
                    borderWidth: "0",
                    topBorderRadius: "0",
                },
                last: {
                    borderWidth: "0",
                    bottomBorderRadius: "0",
                }
            },
            item: {
                color: "var(--text-color-secondary)",
                focusColor: "var(--text-color-secondary)",
                focusBackground: "var(--darker-gray)",
                padding: "1rem 1.25rem",
                borderRadius: "0",
                icon: {
                    color: "var(--text-color-secondary)",
                    focusColor: "var(--text-color-secondary)",
                },
            },
            submenu: {
                indent: "0"
            },
            submenuIcon: {
                color: "var(--text-color-secondary)",
                focusColor: "var(--text-color-secondary)"
            },
            css: () => `
                .p-panelmenu-header-link {
                    display: flex;
                    flex-direction: row-reverse;
                    justify-content: left;

                    span {
                        font-weight: bold;
                    }
                }

                .p-panelmenu-content {
                    background: var(--dark-gray);
                }

                .p-panelmenu-item-content {

                    .p-panelmenu-item-link {
                        padding: .75rem 1rem;
                    }
                }
                
                .p-panelmenu-item-link-active span {
                    color: var(--primary-color);
                    font-weight: bold;
                }
            `,
        }
    }
};

export const appPreset = definePreset(Aura, customTheme);